export type FontFallbackCategory = 'sans-serif' | 'serif' | 'monospace';

export interface PredefinedFontOption {
  family: string;
  fallback: FontFallbackCategory;
  source: 'google' | 'local' | 'standard';
}

const FONT_FAMILY_NAME_REGEX = /^[A-Za-z0-9][A-Za-z0-9 -]{0,63}$/;
const GENERIC_FONT_FAMILIES = new Set<string>([
  'serif',
  'sans-serif',
  'monospace',
  'cursive',
  'fantasy',
  'system-ui',
]);
const FONT_STYLE_SUFFIXES = [
  'Thin',
  'ExtraLight',
  'Light',
  'Regular',
  'Medium',
  'SemiBold',
  'Bold',
  'ExtraBold',
  'Black',
  'Italic',
  'ThinItalic',
  'ExtraLightItalic',
  'LightItalic',
  'MediumItalic',
  'SemiBoldItalic',
  'BoldItalic',
  'ExtraBoldItalic',
  'BlackItalic',
  'Roman',
  'Variable',
] as const;
const UPPERCASE_FONT_TOKENS = new Set<string>(['DM', 'IBM', 'PT', 'UI', 'OS', 'MS']);

export const STANDARD_FONT_OPTIONS: PredefinedFontOption[] = [
  { family: 'system-ui', fallback: 'sans-serif', source: 'standard' },
  { family: 'Arial', fallback: 'sans-serif', source: 'standard' },
  { family: 'Helvetica', fallback: 'sans-serif', source: 'standard' },
  { family: 'Times New Roman', fallback: 'serif', source: 'standard' },
  { family: 'Georgia', fallback: 'serif', source: 'standard' },
  { family: 'Verdana', fallback: 'sans-serif', source: 'standard' },
  { family: 'Tahoma', fallback: 'sans-serif', source: 'standard' },
  { family: 'Trebuchet MS', fallback: 'sans-serif', source: 'standard' },
  { family: 'Courier New', fallback: 'monospace', source: 'standard' },
];

export const LOCAL_FONT_OPTIONS: PredefinedFontOption[] = [
  { family: 'Libre Franklin', fallback: 'sans-serif', source: 'local' },
  { family: 'Lato', fallback: 'sans-serif', source: 'local' },
  { family: 'Ayer', fallback: 'serif', source: 'local' },
  { family: 'Nib', fallback: 'serif', source: 'local' },
];

export const GOOGLE_FONT_OPTIONS: PredefinedFontOption[] = [
  { family: 'Work Sans', fallback: 'sans-serif', source: 'google' },
  { family: 'Lora', fallback: 'serif', source: 'google' },
  { family: 'DM Sans', fallback: 'sans-serif', source: 'google' },
  { family: 'Poppins', fallback: 'sans-serif', source: 'google' },
  { family: 'Open Sans', fallback: 'sans-serif', source: 'google' },
  { family: 'Roboto', fallback: 'sans-serif', source: 'google' },
  { family: 'Merriweather', fallback: 'serif', source: 'google' },
  { family: 'Source Sans 3', fallback: 'sans-serif', source: 'google' },
  { family: 'Nunito Sans', fallback: 'sans-serif', source: 'google' },
  { family: 'Playfair Display', fallback: 'serif', source: 'google' },
  { family: 'Libre Baskerville', fallback: 'serif', source: 'google' },
  { family: 'PT Serif', fallback: 'serif', source: 'google' },
  { family: 'Inter', fallback: 'sans-serif', source: 'google' },
  { family: 'Bitter', fallback: 'serif', source: 'google' },
  { family: 'Manrope', fallback: 'sans-serif', source: 'google' },
  { family: 'Montserrat', fallback: 'sans-serif', source: 'google' },
  { family: 'Oswald', fallback: 'sans-serif', source: 'google' },
  { family: 'Raleway', fallback: 'sans-serif', source: 'google' },
];

const STANDARD_FONT_FAMILIES = new Set<string>(
  STANDARD_FONT_OPTIONS.map((font) => font.family.toLowerCase())
);
const LOCAL_FONT_FAMILIES = new Set<string>(
  LOCAL_FONT_OPTIONS.map((font) => font.family.toLowerCase())
);

export function buildFontTokenValue(
  family: string,
  fallback: FontFallbackCategory = 'sans-serif'
): string {
  if (family === 'system-ui') {
    return `system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, ${fallback}, 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol'`;
  }
  return `'${family}', ${fallback}`;
}

export function parseFontFamilies(fontValue: string): string[] {
  if (!fontValue) return [];

  return fontValue
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean)
    .map((part) => normalizeFontFamilyName(part))
    .filter((part): part is string => Boolean(part));
}

export function extractPrimaryFontFamily(fontValue: string): string | null {
  const families = parseFontFamilies(fontValue);
  return families.length > 0 ? families[0] : null;
}

function splitFontStemIntoWords(input: string): string[] {
  const spaced = input
    .replace(/([a-z])([A-Z0-9])/g, '$1 $2')
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
    .replace(/([0-9])([A-Za-z])/g, '$1 $2')
    .replace(/([A-Za-z])([0-9])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  if (!spaced) return [];

  return spaced.split(' ').filter(Boolean);
}

function normalizeFontWord(word: string): string {
  if (/^\d+$/.test(word)) return word;

  const upper = word.toUpperCase();
  if (UPPERCASE_FONT_TOKENS.has(upper)) {
    return upper;
  }

  return upper.charAt(0) + upper.slice(1).toLowerCase();
}

function stripFontStemSuffixes(stem: string): string {
  let next = stem
    .replace(/\[[^\]]+\]/g, '')
    .replace(/(?:^|[-_\s])VariableFont(?:[-_\s][A-Za-z0-9,]+)?$/i, '')
    .replace(/(?:^|[-_\s])VF(?:[-_\s][A-Za-z0-9,]+)?$/i, '')
    .trim();

  let changed = true;
  while (changed && next) {
    changed = false;
    for (const suffix of FONT_STYLE_SUFFIXES) {
      const suffixPattern = new RegExp(`(?:[-_\\s])${suffix}$`, 'i');
      const compactPattern = new RegExp(`${suffix}$`, 'i');

      if (suffixPattern.test(next)) {
        next = next.replace(suffixPattern, '').trim();
        changed = true;
        break;
      }

      if (compactPattern.test(next)) {
        const stripped = next.replace(compactPattern, '');
        if (/[A-Za-z]/.test(stripped)) {
          next = stripped.trim();
          changed = true;
          break;
        }
      }
    }
  }

  return next;
}

export function deriveFontFamilyFromFileName(fileName: string): string {
  const base = fileName.replace(/\.[^.]+$/, '').trim();
  const stripped = stripFontStemSuffixes(base);
  const stem = stripped || base;
  const words = splitFontStemIntoWords(stem).map(normalizeFontWord);
  const family = words.join(' ').trim();

  return family.slice(0, 64) || 'Custom Font';
}

export function buildFontStorageSlug(input: string): string {
  const base = input
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

  return base || 'custom-font';
}

export function inferFontFallbackCategory(family: string): FontFallbackCategory {
  const normalized = family.trim().toLowerCase();
  if (normalized.includes('mono')) {
    return 'monospace';
  }
  if (normalized.includes('serif')) {
    return 'serif';
  }
  return 'sans-serif';
}

export function isGenericFontFamily(input: string): boolean {
  return GENERIC_FONT_FAMILIES.has(input.trim().toLowerCase());
}

export function isStandardFontFamily(input: string): boolean {
  return STANDARD_FONT_FAMILIES.has(input.trim().toLowerCase());
}

export function isLocalFontFamily(input: string): boolean {
  return LOCAL_FONT_FAMILIES.has(input.trim().toLowerCase());
}

export function isKnownBuiltInFontFamily(input: string): boolean {
  return isGenericFontFamily(input) || isStandardFontFamily(input) || isLocalFontFamily(input);
}

export function parseGoogleFontInput(input: string): string | null {
  const trimmed = input.trim();
  if (!trimmed) return null;

  const normalizeGoogleFamily = (value: string): string | null => {
    const decoded = decodeURIComponent(value).replace(/\+/g, ' ').trim();
    const withoutAxis = decoded.split(':')[0]?.trim() ?? '';
    return normalizeFontFamilyName(withoutAxis);
  };

  try {
    const url = new URL(trimmed);

    if (url.hostname === 'fonts.googleapis.com') {
      const family = url.searchParams.get('family');
      return family ? normalizeGoogleFamily(family) : null;
    }

    if (url.hostname === 'fonts.google.com') {
      const specimenPath = url.pathname.match(/\/specimen\/([^/]+)/i)?.[1];
      return specimenPath ? normalizeGoogleFamily(specimenPath) : null;
    }
  } catch {
    return normalizeFontFamilyName(trimmed);
  }

  return normalizeFontFamilyName(trimmed);
}

export function getRemoteFontFamilies(
  tokens: Partial<Record<'--font-family-title' | '--font-family-body', string>>,
  uploadedFontFamilies: string[] = []
): string[] {
  const remoteFamilies = new Set<string>();
  const uploadedFamilies = new Set(uploadedFontFamilies.map((family) => family.toLowerCase()));

  for (const token of ['--font-family-title', '--font-family-body'] as const) {
    const family = extractPrimaryFontFamily(tokens[token] ?? '');
    if (!family) continue;
    if (!isSafeFontFamilyName(family)) continue;
    if (isKnownBuiltInFontFamily(family)) continue;
    if (uploadedFamilies.has(family.toLowerCase())) continue;
    remoteFamilies.add(family);
  }

  return Array.from(remoteFamilies);
}

export function normalizeFontFamilyName(input: string): string | null {
  const trimmed = input.trim();
  if (!trimmed) return null;

  const unquoted = trimmed.replace(/^['"]+|['"]+$/g, '');
  if (!unquoted) return null;

  return isSafeFontFamilyName(unquoted) ? unquoted : null;
}

export function isSafeFontFamilyName(input: string): boolean {
  return FONT_FAMILY_NAME_REGEX.test(input.trim());
}
